import type { NoteEvent } from "../types";
import { type KeySig, keyFromNotes } from "./staff-layout";

// Key resolution for the schematic staff: an authoritative catalog key tag
// ("G major" / "Bb minor" / "F# major") → fifths, else inferred from the notes.
// Extracted from staff.ts to keep the draw-model builder under the size cap.

// Minor maps to its relative major's signature (schematic — no mode distinction).
function parseKeyTable(): Map<string, number> {
  const majors: Record<string, number> = {
    c: 0,
    g: 1,
    d: 2,
    a: 3,
    e: 4,
    b: 5,
    "f#": 6,
    "c#": 7,
    f: -1,
    bb: -2,
    eb: -3,
    ab: -4,
    db: -5,
    gb: -6,
    cb: -7,
  };
  const table = new Map<string, number>();
  for (const [name, fifths] of Object.entries(majors)) {
    table.set(`${name} major`, fifths);
    table.set(`${name}major`, fifths);
  }
  // Minor keys share their relative major's signature (relative major = minor + 3 semis).
  const minorToMajor: Record<string, string> = {
    a: "c",
    e: "g",
    b: "d",
    "f#": "a",
    "c#": "e",
    "g#": "b",
    "d#": "f#",
    "a#": "c#",
    d: "f",
    g: "bb",
    c: "eb",
    f: "ab",
    bb: "db",
    eb: "gb",
    ab: "cb",
  };
  for (const [minor, major] of Object.entries(minorToMajor)) {
    const f = majors[major];
    if (f !== undefined) {
      table.set(`${minor} minor`, f);
      table.set(`${minor}minor`, f);
    }
  }
  return table;
}

const KEY_NAMES = parseKeyTable();

/** Authoritative key from the catalog tag, else inferred from the notes. */
export function resolveKey(keyHint: string | undefined, notes: readonly NoteEvent[]): KeySig {
  const fromTag = keyHint ? KEY_NAMES.get(keyHint.trim().toLowerCase()) : undefined;
  return fromTag !== undefined ? { fifths: fromTag } : keyFromNotes(notes);
}
